import Order from "../../../models/order/order.model";
import OrderItem from "../../../models/order/orderItem.model";
import roundAbnt from "../../formaters/roundAbnt";

const normalizeOrderItemUnitPrice = (item: OrderItem): OrderItem => {
  if (item.unitario !== 0 || item.unitarioFinal <= 0) {
    return item;
  }

  const unitPrice = roundAbnt(item.unitarioFinal);
  const productsTotal = roundAbnt(unitPrice * item.quantidade);
  const paymentDiscountTotal =
    item.totais.descontos.condicaoPagamento.total || 0;
  const paymentIncreaseTotal =
    item.totais.acrescimos.condicaoPagamento.total || 0;

  return {
    ...item,
    unitario: unitPrice,
    unitarioFinal: unitPrice,
    valorIpi: roundAbnt(unitPrice * item.percentualIpi),
    totais: {
      ...item.totais,
      produtos: productsTotal,
      total: roundAbnt(
        productsTotal - paymentDiscountTotal + paymentIncreaseTotal,
      ),
      descontos: {
        ...item.totais.descontos,
        item: {
          percentual: 0,
          valor: 0,
          total: 0,
        },
        total: paymentDiscountTotal,
      },
      acrescimos: {
        ...item.totais.acrescimos,
        item: {
          percentual: 0,
          valor: 0,
          total: 0,
        },
        total: paymentIncreaseTotal,
      },
    },
  };
};

const adjustmentTotal = (
  productsTotal: number,
  adjustment: {
    percentual: number | null;
    valor: number | null;
    total: number | null;
  },
) => {
  if (adjustment.percentual) {
    return roundAbnt((productsTotal * adjustment.percentual) / 100);
  }

  if (adjustment.valor) {
    return roundAbnt(adjustment.valor);
  }

  return roundAbnt(adjustment.total || 0);
};

const normalizeOrderUnitPrices = (order: Order): Order => {
  let changed = false;
  const itens = order.itens.map((item) => {
    const normalizedItem = normalizeOrderItemUnitPrice(item);
    changed = changed || normalizedItem !== item;
    return normalizedItem;
  });

  if (!changed) {
    return order;
  }

  const productsTotal = roundAbnt(
    itens.reduce((total, item) => total + item.totais.produtos, 0),
  );
  const itemsDiscountTotal = roundAbnt(
    itens.reduce(
      (total, item) => total + (item.totais.descontos.total || 0),
      0,
    ),
  );
  const itemsIncreaseTotal = roundAbnt(
    itens.reduce(
      (total, item) => total + (item.totais.acrescimos.total || 0),
      0,
    ),
  );
  const headerDiscountTotal = adjustmentTotal(
    productsTotal,
    order.totais.descontos.cabecalho,
  );
  const paymentDiscountTotal = adjustmentTotal(
    productsTotal,
    order.totais.descontos.condicaoPagamento,
  );
  const headerIncreaseTotal = adjustmentTotal(
    productsTotal,
    order.totais.acrescimos.cabecalho,
  );
  const paymentIncreaseTotal = adjustmentTotal(
    productsTotal,
    order.totais.acrescimos.condicaoPagamento,
  );
  const discountTotal = roundAbnt(
    itemsDiscountTotal + headerDiscountTotal + paymentDiscountTotal,
  );
  const increaseTotal = roundAbnt(
    itemsIncreaseTotal + headerIncreaseTotal + paymentIncreaseTotal,
  );

  return {
    ...order,
    itens,
    totais: {
      ...order.totais,
      produtos: productsTotal,
      totalIpi: roundAbnt(
        itens.reduce(
          (total, item) => total + item.valorIpi * item.quantidade,
          0,
        ),
      ),
      total: roundAbnt(productsTotal - discountTotal + increaseTotal),
      descontos: {
        ...order.totais.descontos,
        itens: { total: itemsDiscountTotal },
        cabecalho: {
          ...order.totais.descontos.cabecalho,
          total: headerDiscountTotal,
        },
        condicaoPagamento: {
          ...order.totais.descontos.condicaoPagamento,
          total: paymentDiscountTotal,
        },
        total: discountTotal,
      },
      acrescimos: {
        ...order.totais.acrescimos,
        itens: { total: itemsIncreaseTotal },
        cabecalho: {
          ...order.totais.acrescimos.cabecalho,
          total: headerIncreaseTotal,
        },
        condicaoPagamento: {
          ...order.totais.acrescimos.condicaoPagamento,
          total: paymentIncreaseTotal,
        },
        total: increaseTotal,
      },
    },
  };
};

export { normalizeOrderItemUnitPrice };
export default normalizeOrderUnitPrices;
